import { motion } from 'framer-motion';

const stageTransition = {
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
  transition: { duration: 0.4 },
};

export const MathStage = ({ isActive }) => {
  if (!isActive) return null;

  const equation = 'x² + y² = 9';

  return (
    <motion.div className="absolute inset-0 flex items-center justify-center" {...stageTransition}>
      <div className="text-center px-4">
        <div className="font-handwritten text-5xl md:text-7xl text-brand mb-8">
          {equation.split('').map((char, i) => (
            <motion.span
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.15,
                delay: i * 0.08,
                ease: 'easeOut',
              }}
              className="inline-block"
              style={{ minWidth: char === ' ' ? '0.3em' : 'auto' }}
            >
              {char}
            </motion.span>
          ))}
        </div>

        <div className="space-y-3 text-left max-w-xs mx-auto">
          <motion.div
            className="font-handwritten text-2xl text-[#3B82F6]"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: equation.length * 0.08 + 0.5 }}
          >
            → Circle equation
          </motion.div>
          <motion.div
            className="font-handwritten text-2xl text-[#3B82F6]"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: equation.length * 0.08 + 0.9 }}
          >
            → radius = 3
          </motion.div>
          <motion.div
            className="font-handwritten text-2xl text-[#3B82F6]"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: equation.length * 0.08 + 1.3 }}
          >
            → center (0, 0)
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export const GraphStage = ({ isActive }) => {
  if (!isActive) return null;

  return (
    <motion.div className="absolute inset-0 flex items-center justify-center" {...stageTransition}>
      <svg viewBox="0 0 400 400" className="w-full h-full max-w-md max-h-md">
        <defs>
          <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#E2E8F0" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="400" height="400" fill="url(#grid)" />

        <motion.line
          x1="200"
          y1="20"
          x2="200"
          y2="380"
          stroke="#3B444B"
          strokeWidth="2"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.5 }}
        />
        <motion.line
          x1="20"
          y1="200"
          x2="380"
          y2="200"
          stroke="#3B444B"
          strokeWidth="2"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.5 }}
        />

        <motion.circle
          cx="200"
          cy="200"
          r="90"
          fill="none"
          stroke="#3B82F6"
          strokeWidth="3"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
        />

        <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
          <text x="385" y="215" fill="#3B444B" fontWeight="500" fontSize="16">
            x
          </text>
          <text x="210" y="30" fill="#3B444B" fontWeight="500" fontSize="16">
            y
          </text>
        </motion.g>

        <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>
          <line x1="200" y1="200" x2="290" y2="200" stroke="#10B981" strokeWidth="2" strokeDasharray="5,5" />
          <text x="235" y="190" fill="#10B981" fontFamily="Caveat, cursive" fontSize="22">
            r=3
          </text>
        </motion.g>
      </svg>
    </motion.div>
  );
};

// Some code paths in the original project refer to Graph3DStage; keep it for compatibility.
export const Graph3DStage = ({ isActive }) => <PhysicsStage isActive={isActive} />;

export const PhysicsStage = ({ isActive }) => {
  if (!isActive) return null;

  return (
    <motion.div className="absolute inset-0 flex items-center justify-center" {...stageTransition}>
      <div className="relative w-full h-full max-w-md max-h-96">
        <motion.div
          className="absolute w-16 h-16 rounded-full bg-gradient-to-br from-[#F59E0B] to-[#D97706] shadow-lg"
          style={{ left: '50%', marginLeft: '-32px' }}
          animate={{
            // Keep the ball trajectory within the visible whiteboard area.
            // (The container is height-constrained via `max-h-96`, so large pixel
            // translations like 220px can push the ball below the viewport.)
            y: [0, 150, 115, 150, 135, 150],
            scaleY: [1, 1, 0.8, 1, 0.9, 1],
            scaleX: [1, 1, 1.2, 1, 1.1, 1],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatDelay: 0.5,
            ease: 'easeIn',
          }}
        >
          <div className="absolute inset-2 rounded-full bg-gradient-to-br from-white/30 to-transparent" />
        </motion.div>

        <motion.div
          className="absolute top-8 left-8 space-y-2"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="font-handwritten text-2xl text-[#F59E0B]">g = 9.8 m/s²</div>
          <div className="font-handwritten text-xl text-brand/60">Free fall simulation</div>
        </motion.div>

        <motion.svg
          className="absolute top-10 left-1/2 -ml-4"
          width="40"
          height="100"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <defs>
            <marker
              id="arrowhead"
              markerWidth="11"
              markerHeight="8"
              refX="10"
              refY="4"
              markerUnits="strokeWidth"
              orient="auto"
            >
              {/* Tip at (10,4) so it lands exactly on the line endpoint */}
              <polygon points="0 0, 11 4, 0 8" fill="#F59E0B" />
            </marker>
          </defs>
          <line
            x1="20"
            y1="0"
            x2="20"
            y2="85"
            stroke="#F59E0B"
            strokeWidth="2"
            markerEnd="url(#arrowhead)"
          />
        </motion.svg>
      </div>
    </motion.div>
  );
};

export const ChemistryStage = ({ isActive }) => {
  if (!isActive) return null;

  // Benzene ring positions (hexagon) - normalized 0-100
  const atoms = [
    { x: 50, y: 22 },
    { x: 76, y: 39 },
    { x: 76, y: 72 },
    { x: 50, y: 89 },
    { x: 24, y: 72 },
    { x: 24, y: 39 },
  ];

  // SVG positions for bonds
  const svgAtoms = [
    { x: 150, y: 60 },
    { x: 228, y: 105 },
    { x: 228, y: 195 },
    { x: 150, y: 240 },
    { x: 72, y: 195 },
    { x: 72, y: 105 },
  ];

  // Bonds between atoms (including double bonds for benzene)
  const bonds = [
    { from: 0, to: 1, double: true },
    { from: 1, to: 2, double: false },
    { from: 2, to: 3, double: true },
    { from: 3, to: 4, double: false },
    { from: 4, to: 5, double: true },
    { from: 5, to: 0, double: false },
  ];

  return (
    <motion.div className="absolute inset-0 flex flex-col items-center justify-center" {...stageTransition}>
      {/* Molecule container */}
      <div className="relative w-64 h-56" style={{ perspective: '800px' }}>
        {/* Rotating ring structure */}
        <motion.div
          className="absolute inset-0"
          animate={{ rotateY: [0, 360] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
          style={{ transformStyle: 'preserve-3d' }}
        >
          <svg viewBox="0 0 300 270" className="w-full h-full">
            <defs>
              <radialGradient id="carbonSphere3D" cx="30%" cy="25%" r="65%" fx="25%" fy="20%">
                <stop offset="0%" stopColor="#9CA3AF" />
                <stop offset="30%" stopColor="#6B7280" />
                <stop offset="60%" stopColor="#4B5563" />
                <stop offset="85%" stopColor="#374151" />
                <stop offset="100%" stopColor="#1F2937" />
              </radialGradient>

              {/* Bright highlight for 3D effect */}
              <radialGradient id="sphereHighlight3D" cx="25%" cy="20%" r="40%">
                <stop offset="0%" stopColor="white" stopOpacity="0.9" />
                <stop offset="50%" stopColor="white" stopOpacity="0.3" />
                <stop offset="100%" stopColor="white" stopOpacity="0" />
              </radialGradient>

              {/* Rim light effect */}
              <radialGradient id="sphereRimLight" cx="70%" cy="70%" r="50%">
                <stop offset="0%" stopColor="transparent" />
                <stop offset="70%" stopColor="transparent" />
                <stop offset="100%" stopColor="rgba(156, 163, 175, 0.4)" />
              </radialGradient>

              {/* Shadow filter */}
              <filter id="atomShadow3D" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="3" dy="5" stdDeviation="4" floodColor="#1F2937" floodOpacity="0.4" />
              </filter>
            </defs>

            {/* Bonds */}
            {bonds.map((bond, i) => {
              const from = svgAtoms[bond.from];
              const to = svgAtoms[bond.to];
              const dx = to.x - from.x;
              const dy = to.y - from.y;
              const len = Math.sqrt(dx * dx + dy * dy);
              const offsetX = (-dy / len) * 4;
              const offsetY = (dx / len) * 4;

              return (
                <motion.g
                  key={i}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3, delay: i * 0.1 }}
                >
                  <line
                    x1={from.x}
                    y1={from.y}
                    x2={to.x}
                    y2={to.y}
                    stroke="#9CA3AF"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                  {bond.double && (
                    <line
                      x1={from.x + offsetX}
                      y1={from.y + offsetY}
                      x2={to.x + offsetX}
                      y2={to.y + offsetY}
                      stroke="#9CA3AF"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                  )}
                </motion.g>
              );
            })}

            {/* 3D Carbon spheres */}
            {svgAtoms.map((pos, i) => (
              <motion.g
                key={i}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.6 + i * 0.08, type: 'spring', stiffness: 200 }}
                filter="url(#atomShadow3D)"
              >
                {/* Main sphere body */}
                <circle cx={pos.x} cy={pos.y} r="20" fill="url(#carbonSphere3D)" />
                {/* Rim light */}
                <circle cx={pos.x} cy={pos.y} r="20" fill="url(#sphereRimLight)" />
                {/* Primary highlight */}
                <ellipse cx={pos.x - 6} cy={pos.y - 7} rx="8" ry="6" fill="url(#sphereHighlight3D)" />
                {/* Small specular highlight */}
                <circle cx={pos.x - 8} cy={pos.y - 9} r="3" fill="white" fillOpacity="0.7" />
              </motion.g>
            ))}
          </svg>
        </motion.div>

        {/* Counter-rotating "C" labels - these stay readable */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          animate={{ rotateY: [0, 360] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
          style={{ transformStyle: 'preserve-3d' }}
        >
          {atoms.map((pos, i) => (
            <motion.div
              key={i}
              className="absolute"
              style={{
                left: `${pos.x}%`,
                top: `${pos.y}%`,
                transform: 'translate(-50%, -50%)',
                transformStyle: 'preserve-3d',
              }}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.6 + i * 0.08 }}
            >
              <motion.span
                className="text-white text-xs font-bold block"
                style={{
                  textShadow: '0 1px 3px rgba(0,0,0,0.7)',
                  backfaceVisibility: 'hidden',
                }}
                animate={{ rotateY: [0, -360] }}
                transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
              >
                C
              </motion.span>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Static label */}
      <motion.div
        className="mt-2"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2 }}
      >
        <span className="font-handwritten text-2xl text-slate-600">Benzene C₆H₆</span>
      </motion.div>
    </motion.div>
  );
};

export const CollaborationStage = ({ isActive }) => {
  if (!isActive) return null;

  // Cursor component with name tag
  const Cursor = ({ color, name, pathX, pathY, delay }) => (
    <motion.div
      className="absolute"
      style={{ zIndex: 20 }}
      animate={{ x: pathX, y: pathY }}
      transition={{ duration: 4, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut', delay }}
    >
      {/* Cursor arrow */}
      <svg width="14" height="18" viewBox="0 0 14 18" fill="none">
        <path d="M1 1L13 9L7 10L5 17L1 1Z" fill={color} stroke="white" strokeWidth="1" />
      </svg>
      {/* Name label */}
      <span
        className="absolute left-3 top-4 text-[9px] px-2 py-0.5 rounded font-medium text-white whitespace-nowrap"
        style={{ backgroundColor: color }}
      >
        {name}
      </span>
    </motion.div>
  );

  return (
    <motion.div className="absolute inset-0" {...stageTransition}>
      <div className="relative w-full h-full overflow-hidden">
        {/* Alex's sticky note — top left */}
        <motion.div
          className="absolute"
          style={{ top: 20, left: 20, width: 90, height: 70 }}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.4 }}
        >
          <div className="w-full h-full rounded-md bg-blue-50 border border-blue-200 p-2 shadow-sm">
            <motion.div
              className="font-handwritten text-[11px] text-blue-700 leading-tight"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.5 }}
            >
              Key concepts:
              <br />— Gravity
              <br />— Velocity
            </motion.div>
          </div>
        </motion.div>

        {/* Sam's drawing — circle diagram center-right */}
        <motion.svg
          className="absolute"
          style={{ top: 30, right: 30 }}
          width="100"
          height="100"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <motion.circle
            cx="50"
            cy="50"
            r="35"
            fill="none"
            stroke="#A855F7"
            strokeWidth="2"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ delay: 0.6, duration: 1.2 }}
          />
          <motion.line
            x1="50"
            y1="15"
            x2="50"
            y2="85"
            stroke="#A855F7"
            strokeWidth="1"
            strokeDasharray="3,3"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ delay: 1.4, duration: 0.6 }}
          />
          <motion.line
            x1="15"
            y1="50"
            x2="85"
            y2="50"
            stroke="#A855F7"
            strokeWidth="1"
            strokeDasharray="3,3"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ delay: 1.6, duration: 0.6 }}
          />
          <motion.text
            x="50"
            y="95"
            textAnchor="middle"
            fill="#A855F7"
            fontSize="9"
            fontStyle="italic"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.8 }}
          >
            force diagram
          </motion.text>
        </motion.svg>

        {/* Jordan's annotation — bottom area */}
        <motion.div
          className="absolute"
          style={{ bottom: 25, left: 20, maxWidth: 280 }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.4 }}
        >
          <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 shadow-sm">
            <motion.div
              className="font-handwritten text-[11px] text-emerald-700 leading-snug"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1, duration: 0.5 }}
            >
              F = m × a → If mass doubles, force doubles!
            </motion.div>
          </div>
        </motion.div>

        {/* Alex's underline highlight — middle left */}
        <motion.div
          className="absolute"
          style={{ top: 110, left: 25 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
        >
          <div className="font-handwritten text-[11px] text-slate-500">Newton's 2nd Law</div>
          <motion.div
            className="h-[2px] rounded bg-blue-400 mt-0.5"
            initial={{ width: 0 }}
            animate={{ width: 110 }}
            transition={{ delay: 1.5, duration: 0.6 }}
          />
        </motion.div>

        {/* Cursors */}
        <Cursor color="#3B82F6" name="Alex" pathX={[70, 50, 85, 70]} pathY={[55, 70, 60, 55]} delay={0} />
        <Cursor color="#A855F7" name="Sam" pathX={[240, 270, 250, 240]} pathY={[40, 55, 70, 40]} delay={0.5} />
        <Cursor color="#10B981" name="Jordan" pathX={[60, 40, 80, 60]} pathY={[145, 155, 148, 145]} delay={1} />
      </div>
    </motion.div>
  );
};

export const CinematicStage = ({ isActive }) => {
  if (!isActive) return null;

  const planets = [
    { name: 'Mercury', size: 6, orbit: 11, color: '#A0A0A0', duration: 4, startAngle: 0 },
    { name: 'Venus', size: 8, orbit: 16, color: '#E8CDA0', duration: 7, startAngle: 200 },
    { name: 'Earth', size: 9, orbit: 21, color: '#4A90D9', duration: 10, startAngle: 45 },
    { name: 'Mars', size: 7, orbit: 26, color: '#D4613A', duration: 14, startAngle: 150 },
    { name: 'Jupiter', size: 16, orbit: 32, color: '#D4A76A', duration: 24, startAngle: 280 },
    { name: 'Saturn', size: 13, orbit: 38, color: '#E8D8A0', duration: 34, hasRing: true, startAngle: 100 },
    { name: 'Uranus', size: 10, orbit: 43, color: '#A0D8E8', duration: 46, startAngle: 320 },
    { name: 'Neptune', size: 9, orbit: 48, color: '#5B7FC3', duration: 60, startAngle: 210 },
  ];

  return (
    <motion.div
      className="absolute inset-0 flex flex-col items-center justify-center"
      {...stageTransition}
    >
      {/* Solar system container - square, height-constrained */}
      <div className="relative" style={{ height: '78%', aspectRatio: '1' }}>
        {/* Center point */}
        <div className="absolute inset-0">
          {/* Orbit rings */}
          {planets.map((p, i) => (
            <motion.div
              key={`orbit-${i}`}
              className="absolute rounded-full border"
              style={{
                width: `${p.orbit * 2}%`,
                height: `${p.orbit * 2}%`,
                left: `${50 - p.orbit}%`,
                top: `${50 - p.orbit}%`,
                borderColor: 'rgba(203,213,225,0.4)',
                borderStyle: 'dashed',
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.06 * i, duration: 0.4 }}
            />
          ))}

          {/* Sun glow */}
          <div
            className="absolute rounded-full"
            style={{
              width: '10%',
              height: '10%',
              left: '45%',
              top: '45%',
              background: 'radial-gradient(circle, rgba(253,230,138,0.5) 0%, transparent 70%)',
              transform: 'scale(2.5)',
            }}
          />

          {/* Sun */}
          <motion.div
            className="absolute rounded-full"
            style={{
              width: '8%',
              height: '8%',
              left: '46%',
              top: '46%',
              background: 'radial-gradient(circle at 35% 30%, #FDE68A 0%, #FBBF24 50%, #F59E0B 100%)',
              boxShadow: '0 0 20px rgba(251,191,36,0.5)',
            }}
            animate={{ scale: [1, 1.08, 1] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
          />

          {/* Planets */}
          {planets.map((planet, i) => (
            <motion.div
              key={`planet-${i}`}
              className="absolute"
              style={{
                width: `${planet.orbit * 2}%`,
                height: `${planet.orbit * 2}%`,
                left: `${50 - planet.orbit}%`,
                top: `${50 - planet.orbit}%`,
                rotate: planet.startAngle,
              }}
              animate={{ rotate: planet.startAngle + 360 }}
              transition={{ duration: planet.duration, repeat: Infinity, ease: "linear" }}
            >
              {/* Planet positioned at top center of orbit div */}
              <div
                className="absolute rounded-full"
                style={{
                  width: `${planet.size}px`,
                  height: `${planet.size}px`,
                  left: '50%',
                  top: 0,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: planet.color,
                  boxShadow: `0 0 6px ${planet.color}40`,
                }}
              />
              {/* Saturn ring */}
              {planet.hasRing && (
                <div
                  className="absolute"
                  style={{
                    width: `${planet.size + 12}px`,
                    height: '4px',
                    left: '50%',
                    top: 0,
                    transform: 'translate(-50%, -50%)',
                    borderRadius: '50%',
                    border: '1.5px solid rgba(200,184,128,0.7)',
                  }}
                />
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Label */}
      <motion.div
        className="mt-1"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <span className="font-handwritten text-2xl text-brand/50">
          The Solar System
        </span>
      </motion.div>
    </motion.div>
  );
};

